import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { FilterBar } from "../components/FilterBar";
import { SvgLineChart } from "../components/SvgLineChart";
import { BucketToggle, type Bucket, resolveDefaultBucket } from "../components/BucketToggle";
import { CsvExportButton } from "../components/CsvExportButton";
import { ExportSuccessChart } from "../components/ExportSuccessChart";
import { PeakHoursHeatmap } from "../components/PeakHoursHeatmap";
import { OperatorComparePanel } from "../components/OperatorComparePanel";
import { AccuracyTrendChart } from "../components/AccuracyTrendChart";
import { ConfusionPairsTable } from "../components/ConfusionPairsTable";
import { BatchSlaTable } from "../components/BatchSlaTable";
import {
  BottleneckHeatmapView,
  type BottleneckRowView,
} from "@/features/ops/components/BottleneckHeatmap";
import { useThroughput } from "../hooks/useThroughput";
import {
  useOperatorPerformance,
  type OperatorStat,
} from "../hooks/useOperatorPerformance";
import { useOcrAccuracy } from "../hooks/useOcrAccuracy";
import { useSlaCompliance } from "../hooks/useSlaCompliance";
import { useExportSuccess } from "../hooks/useExportSuccess";
import { usePeakHours } from "../hooks/usePeakHours";
import { useOperatorCompare } from "../hooks/useOperatorCompare";
import { useConfusionPairs } from "../hooks/useConfusionPairs";
import { useBatchSla } from "../hooks/useBatchSla";
import { useBottleneckHeatmap } from "../hooks/useBottleneckHeatmap";
import "./AnalyticsPage.css";

type Tab = "Throughput" | "Operator Performance" | "OCR Accuracy" | "SLA Compliance";
const TABS: Tab[] = ["Throughput", "Operator Performance", "OCR Accuracy", "SLA Compliance"];

/** Map each tab to its backend endpoint for the CSV exporter. */
const TAB_ENDPOINT: Record<Tab, string> = {
  Throughput: "/api/analytics/throughput",
  "Operator Performance": "/api/analytics/operator-performance",
  "OCR Accuracy": "/api/analytics/ocr-accuracy",
  "SLA Compliance": "/api/analytics/sla-compliance",
};

const TAB_FILENAME: Record<Tab, string> = {
  Throughput: "throughput.csv",
  "Operator Performance": "operator-performance.csv",
  "OCR Accuracy": "ocr-accuracy.csv",
  "SLA Compliance": "sla-compliance.csv",
};

function getDefaultFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function getDefaultTo(): string {
  return new Date().toISOString().slice(0, 10);
}

function diffDaysInclusive(fromIso?: string, toIso?: string): number {
  if (!fromIso || !toIso) return 30;
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 30;
  return Math.max(1, Math.round((to - from) / 86_400_000) + 1);
}

function rateColor(rate: number, goodThreshold = 95, warnThreshold = 85): string {
  if (rate >= goodThreshold) return "analytics-stat-value--good";
  if (rate >= warnThreshold) return "analytics-stat-value--warn";
  return "analytics-stat-value--bad";
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

function formatRate(r: number): string {
  return `${(r * 100).toFixed(1)}%`;
}

function TierChip({ tier }: { tier?: "hot" | "cold" }) {
  if (tier !== "cold") return null;
  return (
    <span
      className="analytics-tier-chip"
      data-testid="analytics-tier-chip"
      title="Ranges > 90 days roll up to monthly totals. Detailed drill-through is available for the last 90 days."
    >
      Monthly rollup
    </span>
  );
}

// ── Throughput Tab ─────────────────────────────────────────────────────────────
function ThroughputTab({
  jobTypeId,
  from,
  to,
  bucket,
}: {
  jobTypeId?: string;
  from?: string;
  to?: string;
  bucket: Bucket;
}) {
  const throughput = useThroughput(jobTypeId, from, to, bucket);
  const exportSuccess = useExportSuccess(from, to, bucket);
  const peakHours = usePeakHours(from, to);

  if (throughput.isLoading) return <div className="analytics-loading">Loading…</div>;
  if (throughput.error) return <div className="analytics-error">{throughput.error}</div>;

  const data = throughput.data;
  const points = data?.data ?? [];
  const totalDocs = points.reduce((sum, p) => sum + p.count, 0);
  const avgPerBucket = points.length > 0 ? (totalDocs / points.length).toFixed(1) : "0";
  const peakDay = points.reduce(
    (acc, p) => (p.count > acc.count ? p : acc),
    { date: "—", count: 0 } as { date: string; count: number },
  );
  const exportRows = exportSuccess.data?.rows ?? [];
  const exportTotal = exportRows.reduce((sum, r) => sum + r.total, 0);
  const exportSucceeded = exportRows.reduce((sum, r) => sum + r.succeeded, 0);
  const exportRatePct = exportTotal > 0
    ? ((exportSucceeded / exportTotal) * 100).toFixed(1)
    : "—";

  const chartData = points.map((p) => ({ label: p.date.slice(5), value: p.count }));
  const effectiveBucket = data?.bucket ?? bucket;

  return (
    <div className="analytics-panel">
      <div className="analytics-stats-row">
        <div className="analytics-stat-card">
          <div className="analytics-stat-value">{totalDocs.toLocaleString()}</div>
          <div className="analytics-stat-label">Total Documents</div>
        </div>
        <div className="analytics-stat-card">
          <div className="analytics-stat-value">{avgPerBucket}</div>
          <div className="analytics-stat-label">Avg Docs / {effectiveBucket}</div>
        </div>
        <div className="analytics-stat-card">
          <div className="analytics-stat-value">
            {peakDay.count > 0 ? peakDay.count.toLocaleString() : "—"}
          </div>
          <div className="analytics-stat-label">
            Peak {peakDay.date !== "—" ? peakDay.date.slice(5) : ""}
          </div>
        </div>
        <div className="analytics-stat-card">
          <div className="analytics-stat-value">{exportRatePct}%</div>
          <div className="analytics-stat-label">Export Success Rate</div>
        </div>
      </div>

      <div className="analytics-section-title">
        Throughput ({effectiveBucket})
        <TierChip tier={data?.tier} />
      </div>
      <div className="analytics-chart-area">
        <SvgLineChart data={chartData} color="#ea580c" height={220} />
      </div>

      <div className="analytics-section-title">
        Export Success by Destination
        <TierChip tier={exportSuccess.data?.tier} />
      </div>
      {exportSuccess.isLoading ? (
        <div className="analytics-loading">Loading…</div>
      ) : exportSuccess.error ? (
        <div className="analytics-error">{exportSuccess.error}</div>
      ) : (
        <ExportSuccessChart rows={exportRows} />
      )}

      <div className="analytics-section-title">Peak Hours (UTC)</div>
      {peakHours.isLoading ? (
        <div className="analytics-loading">Loading…</div>
      ) : peakHours.error ? (
        <div className="analytics-error">{peakHours.error}</div>
      ) : (
        <PeakHoursHeatmap grid={peakHours.data?.grid ?? []} />
      )}
    </div>
  );
}

// ── Operator Performance Tab ───────────────────────────────────────────────────
const MAX_COMPARE = 4;

function OperatorPerformanceTab({
  jobTypeId,
  from,
  to,
  bucket,
}: {
  jobTypeId?: string;
  from?: string;
  to?: string;
  bucket: Bucket;
}) {
  const { data, isLoading, error } = useOperatorPerformance(jobTypeId, from, to, bucket);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [compareError, setCompareError] = useState<string | null>(null);

  // Server only accepts 1..4. We fetch only when 2+ are selected.
  const compareTrend = useOperatorCompare(compareIds.length >= 2 ? compareIds : [], from, to);

  if (isLoading) return <div className="analytics-loading">Loading…</div>;
  if (error) return <div className="analytics-error">{error}</div>;

  const operators = data?.operators ?? [];

  function toggleCompare(opId: string) {
    setCompareError(null);
    setCompareIds((prev) => {
      if (prev.includes(opId)) return prev.filter((id) => id !== opId);
      if (prev.length >= MAX_COMPARE) {
        setCompareError(`Compare tray is limited to ${MAX_COMPARE} operators.`);
        return prev;
      }
      return [...prev, opId];
    });
  }

  const compareRows = (compareTrend.data?.rows ?? []).map((r) => {
    const op = operators.find((o) => o.operatorId === r.operatorId);
    return {
      operatorId: r.operatorId,
      operatorName: op?.operatorName ?? r.operatorId,
      docs: r.docs,
      avgKpm: r.avgKpm,
      avgCorrectionRate: r.avgCorrectionRate,
      avgTimePerFieldMs: r.avgTimePerFieldMs,
    };
  });

  return (
    <div className="analytics-panel">
      <div className="analytics-section-title">
        Operators
        <TierChip tier={data?.tier} />
      </div>
      {compareError && (
        <div className="analytics-error" role="alert">
          {compareError}
        </div>
      )}
      {operators.length === 0 ? (
        <div className="analytics-empty">No operator data for this period.</div>
      ) : (
        <table className="analytics-table">
          <thead>
            <tr>
              <th aria-label="Compare">⇌</th>
              <th>Operator</th>
              <th>Docs Processed</th>
              <th>Avg Time</th>
              <th>Avg KPM</th>
              <th>Avg Time / Field</th>
              <th>Total Keystrokes</th>
              <th>Correction Rate</th>
              <th>Override Rate</th>
            </tr>
          </thead>
          <tbody>
            {operators.map((op: OperatorStat) => {
              const checked = compareIds.includes(op.operatorId);
              return (
                <tr key={op.operatorId}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Compare ${op.operatorName}`}
                      checked={checked}
                      onChange={() => toggleCompare(op.operatorId)}
                    />
                  </td>
                  <td style={{ color: "var(--color-text-primary)" }}>
                    <Link
                      to={`/analytics/operators/${op.operatorId}`}
                      className="analytics-link"
                    >
                      {op.operatorName}
                    </Link>
                  </td>
                  <td>{op.docsProcessed.toLocaleString()}</td>
                  <td>{formatDuration(op.avgDurationMs)}</td>
                  <td>{op.avgKpm != null ? op.avgKpm.toFixed(1) : "—"}</td>
                  <td>
                    {op.avgTimePerFieldMs != null && op.avgTimePerFieldMs > 0
                      ? `${Math.round(op.avgTimePerFieldMs)}ms`
                      : "—"}
                  </td>
                  <td>
                    {op.totalKeystrokes != null
                      ? op.totalKeystrokes.toLocaleString()
                      : "—"}
                  </td>
                  <td>{formatRate(op.correctionRate)}</td>
                  <td>{formatRate(op.overrideRate)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <OperatorComparePanel
        rows={compareRows}
        onDismiss={() => setCompareIds([])}
      />
    </div>
  );
}

// ── OCR Accuracy Tab ───────────────────────────────────────────────────────────
function OcrAccuracyTab({
  jobTypeId,
  from,
  to,
  bucket,
}: {
  jobTypeId?: string;
  from?: string;
  to?: string;
  bucket: Bucket;
}) {
  const { data, isLoading, error } = useOcrAccuracy(jobTypeId, from, to, bucket);
  const confusion = useConfusionPairs(jobTypeId, from, to, bucket);

  if (isLoading) return <div className="analytics-loading">Loading…</div>;
  if (error) return <div className="analytics-error">{error}</div>;

  const overall = data?.overall;
  const byClass = data?.byClassification ?? [];
  const trend = data?.trend ?? [];

  const accuracyPct = overall ? (overall.accuracy * 100).toFixed(1) : "—";
  const colorClass = overall ? rateColor(overall.accuracy * 100) : "";

  // "Hotspot" fields = classifications ranked by correction count (desc), top 5.
  const hotspots = byClass
    .slice()
    .sort((a, b) => b.totalCorrections - a.totalCorrections)
    .slice(0, 5);

  return (
    <div className="analytics-panel">
      <div className="analytics-stats-row">
        <div className="analytics-stat-card">
          <div className={`analytics-stat-value ${colorClass}`}>{accuracyPct}%</div>
          <div className="analytics-stat-label">Overall Accuracy</div>
        </div>
        {overall && (
          <>
            <div className="analytics-stat-card">
              <div className="analytics-stat-value">{overall.totalFields.toLocaleString()}</div>
              <div className="analytics-stat-label">Total Fields</div>
            </div>
            <div className="analytics-stat-card">
              <div className="analytics-stat-value">{overall.totalCorrections.toLocaleString()}</div>
              <div className="analytics-stat-label">Corrections</div>
            </div>
            <div className="analytics-stat-card">
              <div className="analytics-stat-value">{overall.docsProcessed.toLocaleString()}</div>
              <div className="analytics-stat-label">Docs Processed</div>
            </div>
          </>
        )}
      </div>

      <div className="analytics-section-title">
        Accuracy Trend
        <TierChip tier={data?.tier} />
      </div>
      <AccuracyTrendChart trend={trend} />

      {hotspots.length > 0 && (
        <>
          <div className="analytics-section-title">Field Hotspots (Top 5 by corrections)</div>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Classification</th>
                <th>Accuracy</th>
                <th>Corrections</th>
                <th>Total Fields</th>
              </tr>
            </thead>
            <tbody>
              {hotspots.map((c) => {
                const pct = (c.accuracy * 100).toFixed(1);
                const color =
                  c.accuracy >= 0.95
                    ? "var(--color-success)"
                    : c.accuracy >= 0.85
                    ? "var(--color-warning)"
                    : "var(--color-error)";
                return (
                  <tr key={c.classification}>
                    <td style={{ color: "var(--color-text-primary)" }}>{c.classification}</td>
                    <td style={{ color }}>{pct}%</td>
                    <td style={{ color: "var(--color-error)" }}>
                      {c.totalCorrections.toLocaleString()}
                    </td>
                    <td>{c.totalFields.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      <div className="analytics-section-title">
        Confused Pairs
        <TierChip tier={confusion.data?.tier} />
      </div>
      {confusion.isLoading ? (
        <div className="analytics-loading">Loading…</div>
      ) : confusion.error ? (
        <div className="analytics-error">{confusion.error}</div>
      ) : (
        <ConfusionPairsTable
          rows={confusion.data?.rows ?? []}
          tier={confusion.data?.tier}
        />
      )}

      {byClass.length > 0 && (
        <>
          <div className="analytics-section-title">
            By Classification
            <TierChip tier={data?.tier} />
          </div>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Classification</th>
                <th>Accuracy</th>
                <th>Total Fields</th>
                <th>Corrections</th>
                <th>Docs</th>
              </tr>
            </thead>
            <tbody>
              {byClass.map((c) => {
                const pct = (c.accuracy * 100).toFixed(1);
                const cc = rateColor(c.accuracy * 100);
                return (
                  <tr key={c.classification}>
                    <td style={{ color: "var(--color-text-primary)" }}>{c.classification}</td>
                    <td
                      className={cc.replace("analytics-stat-value--", "color-")}
                      style={{
                        color: cc.includes("good")
                          ? "var(--color-success)"
                          : cc.includes("warn")
                          ? "var(--color-warning)"
                          : "var(--color-error)",
                      }}
                    >
                      {pct}%
                    </td>
                    <td>{c.totalFields.toLocaleString()}</td>
                    <td>{c.totalCorrections.toLocaleString()}</td>
                    <td>{c.docsProcessed.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ── SLA Compliance Tab ─────────────────────────────────────────────────────────
function SlaComplianceTab({
  jobTypeId,
  from,
  to,
  bucket,
}: {
  jobTypeId?: string;
  from?: string;
  to?: string;
  bucket: Bucket;
}) {
  const { data, isLoading, error } = useSlaCompliance(jobTypeId, from, to, bucket);
  const batchSla = useBatchSla(jobTypeId, from, to, bucket);
  const bottleneck = useBottleneckHeatmap(from, to, bucket);

  if (isLoading) return <div className="analytics-loading">Loading…</div>;
  if (error) return <div className="analytics-error">{error}</div>;

  const overall = data?.overall;
  const byJobType = data?.byJobType ?? [];

  const compliancePct = overall ? (overall.complianceRate * 100).toFixed(1) : "—";
  const colorClass = overall ? rateColor(overall.complianceRate * 100) : "";

  const bottleneckRows: BottleneckRowView[] = (bottleneck.data?.rows ?? [])
    .slice()
    .sort((a, b) => b.avgDurationMs - a.avgDurationMs)
    .map((r) => ({
      step: r.step,
      magnitude: r.avgDurationMs,
      magnitudeLabel: `${r.docCount.toLocaleString()} docs`,
      avgLabel:
        r.avgDurationMs >= 60000
          ? `${(r.avgDurationMs / 60000).toFixed(1)}m`
          : `${Math.round(r.avgDurationMs / 1000)}s`,
      warn: r.ratio != null && r.ratio > 1.5,
    }));

  return (
    <div className="analytics-panel">
      <div className="analytics-stats-row">
        <div className="analytics-stat-card">
          <div className={`analytics-stat-value ${colorClass}`}>{compliancePct}%</div>
          <div className="analytics-stat-label">Compliance Rate</div>
        </div>
        {overall && (
          <>
            <div className="analytics-stat-card">
              <div className="analytics-stat-value analytics-stat-value--good">
                {overall.metCount.toLocaleString()}
              </div>
              <div className="analytics-stat-label">Met SLA</div>
            </div>
            <div className="analytics-stat-card">
              <div className="analytics-stat-value analytics-stat-value--bad">
                {overall.missedCount.toLocaleString()}
              </div>
              <div className="analytics-stat-label">Missed SLA</div>
            </div>
            <div className="analytics-stat-card">
              <div className="analytics-stat-value">{overall.total.toLocaleString()}</div>
              <div className="analytics-stat-label">Total</div>
            </div>
          </>
        )}
      </div>

      <div className="analytics-section-title">
        Per-Batch SLA
        <TierChip tier={batchSla.data?.tier} />
      </div>
      {batchSla.isLoading ? (
        <div className="analytics-loading">Loading…</div>
      ) : batchSla.error ? (
        <div className="analytics-error">{batchSla.error}</div>
      ) : (
        <BatchSlaTable rows={batchSla.data?.rows ?? []} />
      )}

      <div className="analytics-section-title">
        Step Duration Heatmap
        <TierChip tier={bottleneck.data?.tier} />
      </div>
      {bottleneck.isLoading ? (
        <div className="analytics-loading">Loading…</div>
      ) : bottleneck.error ? (
        <div className="analytics-error">{bottleneck.error}</div>
      ) : (
        <BottleneckHeatmapView
          rows={bottleneckRows}
          emptyMessage="No step duration data for this period."
          testId="analytics-bottleneck-heatmap"
        />
      )}

      {byJobType.length > 0 && (
        <>
          <div className="analytics-section-title">
            By Job Type
            <TierChip tier={data?.tier} />
          </div>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Job Type</th>
                <th>Compliance Rate</th>
                <th>Met</th>
                <th>Missed</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {byJobType.map((jt) => {
                const pct = (jt.complianceRate * 100).toFixed(1);
                const rate = jt.complianceRate * 100;
                const color =
                  rate >= 95
                    ? "var(--color-success)"
                    : rate >= 85
                    ? "var(--color-warning)"
                    : "var(--color-error)";
                return (
                  <tr key={jt.jobTypeId}>
                    <td style={{ color: "var(--color-text-primary)" }}>{jt.jobTypeName}</td>
                    <td style={{ color }}>{pct}%</td>
                    <td style={{ color: "var(--color-success)" }}>{jt.metCount.toLocaleString()}</td>
                    <td style={{ color: "var(--color-error)" }}>{jt.missedCount.toLocaleString()}</td>
                    <td>{jt.total.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState<Tab>("Throughput");
  const [jobTypeId, setJobTypeId] = useState<string | undefined>(undefined);
  const [from, setFrom] = useState<string | undefined>(getDefaultFrom());
  const [to, setTo] = useState<string | undefined>(getDefaultTo());

  const rangeDays = useMemo(() => diffDaysInclusive(from, to), [from, to]);
  const [bucket, setBucket] = useState<Bucket>(() => resolveDefaultBucket(rangeDays));

  // Keep default-bucket in sync with range when the user hasn't manually picked.
  // Simplest: recompute when range crosses the 90-day boundary.
  const [bucketLockedByUser, setBucketLockedByUser] = useState(false);
  useMemoRangeSync({ rangeDays, bucketLockedByUser, setBucket });

  function handleBucketChange(next: Bucket) {
    setBucketLockedByUser(true);
    setBucket(next);
  }

  function handleFilter(
    newJobTypeId: string | undefined,
    newFrom: string | undefined,
    newTo: string | undefined,
  ) {
    setJobTypeId(newJobTypeId);
    setFrom(newFrom ?? getDefaultFrom());
    setTo(newTo ?? getDefaultTo());
  }

  const csvParams: Record<string, string> = { bucket };
  if (from) csvParams.from = from;
  if (to) csvParams.to = to;
  if (jobTypeId) csvParams.jobTypeId = jobTypeId;

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h1 className="analytics-title">Analytics</h1>
      </div>

      <FilterBar jobTypeId={jobTypeId} from={from} to={to} onFilter={handleFilter} />

      <div className="analytics-page__header-actions">
        <BucketToggle value={bucket} onChange={handleBucketChange} rangeDays={rangeDays} />
        <CsvExportButton
          endpoint={TAB_ENDPOINT[activeTab]}
          params={csvParams}
          filename={TAB_FILENAME[activeTab]}
        />
      </div>

      <div className="analytics-tab-bar">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`analytics-tab-btn ${activeTab === tab ? "analytics-tab-btn--active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "Throughput" && (
        <ThroughputTab jobTypeId={jobTypeId} from={from} to={to} bucket={bucket} />
      )}
      {activeTab === "Operator Performance" && (
        <OperatorPerformanceTab jobTypeId={jobTypeId} from={from} to={to} bucket={bucket} />
      )}
      {activeTab === "OCR Accuracy" && (
        <OcrAccuracyTab jobTypeId={jobTypeId} from={from} to={to} bucket={bucket} />
      )}
      {activeTab === "SLA Compliance" && (
        <SlaComplianceTab jobTypeId={jobTypeId} from={from} to={to} bucket={bucket} />
      )}
    </div>
  );
}

// Resync the bucket default when the date range changes AND the user hasn't
// locked a choice — e.g. widening the FilterBar window past 90 days.
function useMemoRangeSync({
  rangeDays,
  bucketLockedByUser,
  setBucket,
}: {
  rangeDays: number;
  bucketLockedByUser: boolean;
  setBucket: (b: Bucket) => void;
}) {
  useEffect(() => {
    if (bucketLockedByUser) return;
    setBucket(resolveDefaultBucket(rangeDays));
  }, [rangeDays, bucketLockedByUser, setBucket]);
}
